"use client";

import { useId, useMemo, useState } from "react";
import { ProvenanceBadge } from "./Primitives.web";
import type { KundaliPlanet } from "./types";

/**
 * North Indian (diamond) kundali geometry on a 300×300 canvas.
 *
 * The frame is the outer square, the diamond joining the side midpoints and
 * both corner diagonals: 12 regions. House 1 is the top diamond and houses run
 * counter-clockwise. Badges are packed inside each house polygon, clear of its
 * lines and its number, so two grahas in one house never overlap and no badge
 * sits on a house line (b-calc-08).
 */
type Point = readonly [number, number];

const CHART_SIZE = 300;
const MID = CHART_SIZE / 2;
const QUARTER = CHART_SIZE / 4;
const FAR = CHART_SIZE;

const NORTH_INDIAN_HOUSES: Readonly<Record<number, readonly Point[]>> = {
  1: [
    [MID, 0],
    [QUARTER, QUARTER],
    [MID, MID],
    [FAR - QUARTER, QUARTER],
  ],
  2: [
    [0, 0],
    [MID, 0],
    [QUARTER, QUARTER],
  ],
  3: [
    [0, 0],
    [QUARTER, QUARTER],
    [0, MID],
  ],
  4: [
    [0, MID],
    [QUARTER, QUARTER],
    [MID, MID],
    [QUARTER, FAR - QUARTER],
  ],
  5: [
    [0, MID],
    [QUARTER, FAR - QUARTER],
    [0, FAR],
  ],
  6: [
    [0, FAR],
    [QUARTER, FAR - QUARTER],
    [MID, FAR],
  ],
  7: [
    [MID, FAR],
    [QUARTER, FAR - QUARTER],
    [MID, MID],
    [FAR - QUARTER, FAR - QUARTER],
  ],
  8: [
    [MID, FAR],
    [FAR - QUARTER, FAR - QUARTER],
    [FAR, FAR],
  ],
  9: [
    [FAR, FAR],
    [FAR - QUARTER, FAR - QUARTER],
    [FAR, MID],
  ],
  10: [
    [FAR, MID],
    [FAR - QUARTER, FAR - QUARTER],
    [MID, MID],
    [FAR - QUARTER, QUARTER],
  ],
  11: [
    [FAR, MID],
    [FAR - QUARTER, QUARTER],
    [FAR, 0],
  ],
  12: [
    [FAR, 0],
    [FAR - QUARTER, QUARTER],
    [MID, 0],
  ],
};

/** Badge radius when a house is roomy, and the smaller one for a crowded house. */
const BADGE_RADII = [10, 8, 7] as const;
/** Clearance from a house line, beyond the badge radius (stroke plus air). */
const LINE_CLEARANCE = 1.5;
/** Room kept around a house number so no badge covers it. */
const LABEL_CLEARANCE = 6;
/** How far the number sits from the house's inner vertex (the lagna label is wider). */
const LABEL_INSET = { lagna: 20, other: 13 } as const;
/** Air between two badges, beyond touching. */
const BADGE_GAP = 2;
/** Lattice points generated on each side of a house's centre (covers the canvas). */
const LATTICE_REACH = 8;

type HouseBadgeLayout = { radius: number; centers: Point[] };

function centroid(polygon: readonly Point[]): Point {
  const sum = polygon.reduce<[number, number]>((acc, [x, y]) => [acc[0] + x, acc[1] + y], [0, 0]);
  return [sum[0] / polygon.length, sum[1] / polygon.length];
}

function segmentDistance(point: Point, a: Point, b: Point): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const t = Math.max(
    0,
    Math.min(1, ((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / (dx * dx + dy * dy))
  );
  return Math.hypot(point[0] - (a[0] + t * dx), point[1] - (a[1] + t * dy));
}

function insidePolygon(point: Point, polygon: readonly Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if (
      yi > point[1] !== yj > point[1] &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function edgeDistance(point: Point, polygon: readonly Point[]): number {
  let nearest = Infinity;
  for (let i = 0; i < polygon.length; i += 1) {
    nearest = Math.min(
      nearest,
      segmentDistance(point, polygon[i], polygon[(i + 1) % polygon.length])
    );
  }
  return nearest;
}

/** Where the house number (or the lagna mark) sits: just inside the vertex nearest the chart centre. */
function houseLabelPoint(house: number): Point {
  const polygon = NORTH_INDIAN_HOUSES[house];
  const middle = centroid(polygon);
  const inner = polygon.reduce((best, vertex) =>
    Math.hypot(vertex[0] - MID, vertex[1] - MID) < Math.hypot(best[0] - MID, best[1] - MID)
      ? vertex
      : best
  );
  const inset = house === 1 ? LABEL_INSET.lagna : LABEL_INSET.other;
  const length = Math.hypot(middle[0] - inner[0], middle[1] - inner[1]);
  return [
    inner[0] + ((middle[0] - inner[0]) * inset) / length,
    inner[1] + ((middle[1] - inner[1]) * inset) / length,
  ];
}

/**
 * Packs `count` badges into a house on a hexagonal lattice whose spacing is
 * one badge diameter plus a gap, so no two badges can overlap by
 * construction. Only lattice points at least the badge radius (plus
 * clearance) from every house line and from the house number are eligible;
 * the badges take the eligible points nearest the house's centre, lined up
 * along its long side (a row in the wide houses, a column in the tall side
 * triangles 3, 5, 9 and 11). Four lattice phases are tried and the most
 * centred group wins. Null when the house cannot hold `count` at this radius.
 * A few dozen points per phase keeps a whole chart well under a millisecond.
 */
function packHouse(house: number, count: number, radius: number): Point[] | null {
  const polygon = NORTH_INDIAN_HOUSES[house];
  const middle = centroid(polygon);
  const label = houseLabelPoint(house);
  const xs = polygon.map(([x]) => x);
  const ys = polygon.map(([, y]) => y);
  const tall = Math.max(...ys) - Math.min(...ys) > Math.max(...xs) - Math.min(...xs);
  const pitch = radius * 2 + BADGE_GAP;
  const rowHeight = (pitch * Math.sqrt(3)) / 2;
  const fits = (point: Point) =>
    insidePolygon(point, polygon) &&
    edgeDistance(point, polygon) >= radius + LINE_CLEARANCE &&
    Math.hypot(point[0] - label[0], point[1] - label[1]) >= radius + LABEL_CLEARANCE;
  const [wx, wy] = tall ? [1.25, 1] : [1, 1.25];
  const rank = ([x, y]: Point) => Math.hypot((x - middle[0]) * wx, (y - middle[1]) * wy);

  let best: Point[] | null = null;
  let bestScore = Infinity;
  for (const [alongOffset, acrossOffset] of [
    [0, 0],
    [pitch / 2, 0],
    [0, rowHeight / 2],
    [pitch / 2, rowHeight / 2],
  ]) {
    const eligible: Point[] = [];
    for (let row = -LATTICE_REACH; row <= LATTICE_REACH; row += 1) {
      for (let column = -LATTICE_REACH; column <= LATTICE_REACH; column += 1) {
        const along = column * pitch + (row % 2 ? pitch / 2 : 0) + alongOffset;
        const across = row * rowHeight + acrossOffset;
        const point: Point = tall
          ? [roundTenth(middle[0] + across), roundTenth(middle[1] + along)]
          : [roundTenth(middle[0] + along), roundTenth(middle[1] + across)];
        if (fits(point)) eligible.push(point);
      }
    }
    if (eligible.length < count) continue;
    eligible.sort((a, b) => rank(a) - rank(b) || a[0] - b[0] || a[1] - b[1]);
    const chosen = eligible.slice(0, count);
    const group = centroid(chosen);
    const score =
      Math.hypot(group[0] - middle[0], group[1] - middle[1]) * 3 +
      chosen.reduce((sum, point) => sum + rank(point), 0) / count;
    if (score < bestScore) {
      bestScore = score;
      best = chosen;
    }
  }
  return best;
}

function roundTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

const layoutCache = new Map<string, HouseBadgeLayout>();

function layoutHouseBadges(house: number, count: number): HouseBadgeLayout {
  const key = `${house}:${count}`;
  const cached = layoutCache.get(key);
  if (cached) return cached;
  let layout: HouseBadgeLayout = { radius: BADGE_RADII[0], centers: [] };
  if (count > 0 && NORTH_INDIAN_HOUSES[house]) {
    for (const radius of BADGE_RADII) {
      const centers = packHouse(house, count, radius);
      if (centers) {
        layout = { radius, centers };
        break;
      }
    }
    // More bodies than the house holds even at the smallest radius (the engine
    // sends at most nine): draw as many as fit; the graha list names them all.
    const smallest = BADGE_RADII[BADGE_RADII.length - 1];
    for (let fit = count - 1; layout.centers.length === 0 && fit > 0; fit -= 1) {
      const centers = packHouse(house, fit, smallest);
      if (centers) layout = { radius: smallest, centers };
    }
  }
  layoutCache.set(key, layout);
  return layout;
}

type ChartLocale = "en" | "hi";

const PLANET_ABBREVIATIONS: Record<ChartLocale, Record<string, string>> = {
  en: {
    sun: "Su",
    moon: "Mo",
    mars: "Ma",
    mercury: "Bu",
    jupiter: "Ju",
    venus: "Shu",
    saturn: "Sha",
    rahu: "Ra",
    ketu: "Ke",
  },
  hi: {
    sun: "सू",
    moon: "चं",
    mars: "मं",
    mercury: "बु",
    jupiter: "गु",
    venus: "शु",
    saturn: "श",
    rahu: "रा",
    ketu: "के",
  },
};

const HINDI_GRAHA_NAMES: Record<string, string> = {
  sun: "सूर्य",
  moon: "चंद्र",
  mars: "मंगल",
  mercury: "बुध",
  jupiter: "गुरु",
  venus: "शुक्र",
  saturn: "शनि",
  rahu: "राहु",
  ketu: "केतु",
};

const CHART_COPY = {
  en: {
    title: "Janam Kundali",
    subtitle: "North Indian chart · select a graha for details",
    lagna: "Asc",
    chartLabel: "North Indian kundali showing graha positions across 12 houses",
    selectLabel: "Select a graha",
    housePrefix: "H",
    house: "House",
    hint: "Tap or focus a graha to read its chart position.",
    standard: "Standard dignity",
    retrograde: "Retrograde",
    combust: "Combust",
    exalted: "Exalted",
    debilitated: "Debilitated",
    engine: undefined,
  },
  hi: {
    title: "जन्म कुंडली",
    subtitle: "उत्तर भारतीय चार्ट · विवरण के लिए ग्रह चुनें",
    lagna: "लग्न",
    chartLabel: "उत्तर भारतीय कुंडली: 12 भावों में ग्रहों की स्थिति",
    selectLabel: "ग्रह चुनें",
    housePrefix: "भाव ",
    house: "भाव",
    hint: "किसी ग्रह की स्थिति पढ़ने के लिए उसे टैप करें।",
    standard: "सामान्य स्थिति",
    retrograde: "वक्री",
    combust: "अस्त",
    exalted: "उच्च",
    debilitated: "नीच",
    engine: "कुंडली इंजन",
  },
} as const;

type ChartCopy = (typeof CHART_COPY)[ChartLocale];

function grahaName(planetId: string, locale: ChartLocale): string {
  return locale === "hi" ? (HINDI_GRAHA_NAMES[planetId.toLowerCase()] ?? planetId) : planetId;
}

export interface KundaliChartProps {
  planets: readonly KundaliPlanet[];
  /** Defaults to "Janam Kundali" / "जन्म कुंडली" by locale. */
  title?: string;
  /** Defaults to the locale's "select a graha" line. */
  subtitle?: string;
  className?: string;
  onPlanetSelect?: (planet: KundaliPlanet) => void;
  /** "hi" draws Devanagari graha marks and labels; English stays the default. */
  locale?: ChartLocale;
  /**
   * What the selected graha's detail says about a graha whose dignity flags
   * (`is_exalted`, `is_debilitated`) are not given. "standard", the default,
   * reads them as false and prints "Standard dignity" when no status is set.
   * "unknown" prints no dignity for that graha, for a chart whose engine does
   * not compute dignity (the varshphal annual chart). A graha that carries
   * both flags reads the same either way.
   */
  unflaggedDignity?: "standard" | "unknown";
}

export function KundaliChart({
  planets,
  title,
  subtitle,
  className = "",
  onPlanetSelect,
  locale = "en",
  unflaggedDignity = "standard",
}: KundaliChartProps) {
  const copy = CHART_COPY[locale];
  const titleId = useId();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const planetsByHouse = useMemo(() => groupPlanetsByHouse(planets), [planets]);
  const selected = planets.find((planet) => planet.planet_id === selectedId) ?? null;

  function selectPlanet(planet: KundaliPlanet) {
    setSelectedId(String(planet.planet_id));
    onPlanetSelect?.(planet);
  }

  return (
    <section
      className={`jyotish-kundali ${className}`}
      aria-labelledby={titleId}
      lang={locale === "hi" ? "hi" : undefined}
    >
      <header className="jyotish-kundali__header">
        <div>
          <h3 id={titleId}>{title ?? copy.title}</h3>
          <p>{subtitle ?? copy.subtitle}</p>
        </div>
        <ProvenanceBadge kind="engine" label={copy.engine} />
      </header>
      <div className="jyotish-kundali__canvas">
        <svg viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`} role="img" aria-label={copy.chartLabel}>
          <ChartFrame />
          {Array.from({ length: 12 }, (_, index) => index + 1).map((house) => (
            <House
              key={house}
              house={house}
              planets={planetsByHouse[house]}
              selectedId={selectedId}
              locale={locale}
            />
          ))}
        </svg>
      </div>
      <div className="jyotish-kundali__graha-list" aria-label={copy.selectLabel}>
        {planets.map((planet) => {
          const planetId = String(planet.planet_id);
          return (
            <button
              key={planetId}
              type="button"
              aria-pressed={selectedId === planetId}
              onClick={() => selectPlanet(planet)}
            >
              {grahaName(planetId, locale)}
              <span>
                {copy.housePrefix}
                {planet.house ?? "—"}
              </span>
            </button>
          );
        })}
      </div>
      <PlanetDetail
        planet={selected}
        copy={copy}
        locale={locale}
        unflaggedDignity={unflaggedDignity}
      />
    </section>
  );
}

function ChartFrame() {
  return (
    <g className="jyotish-kundali__frame" aria-hidden>
      <rect x="1" y="1" width="298" height="298" rx="3" />
      <polygon points={`${MID},0 0,${MID} ${MID},${CHART_SIZE} ${CHART_SIZE},${MID}`} />
      {/* The two corner diagonals split the square into the 12 North Indian
          houses (a vertical and a horizontal mid-line made only 8). */}
      <line x1="0" y1="0" x2={CHART_SIZE} y2={CHART_SIZE} />
      <line x1={CHART_SIZE} y1="0" x2="0" y2={CHART_SIZE} />
    </g>
  );
}

function House({
  house,
  planets,
  selectedId,
  locale,
}: {
  house: number;
  planets: KundaliPlanet[];
  selectedId: string | null;
  locale: ChartLocale;
}) {
  const label = houseLabelPoint(house);
  const { radius, centers } = layoutHouseBadges(house, planets.length);
  return (
    <g>
      <text
        x={roundTenth(label[0])}
        y={roundTenth(label[1])}
        data-house-label={house}
        className={house === 1 ? "jyotish-kundali__asc" : "jyotish-kundali__house-number"}
      >
        {house === 1 ? CHART_COPY[locale].lagna : house}
      </text>
      {planets.slice(0, centers.length).map((planet, index) => (
        <Planet
          key={String(planet.planet_id)}
          planet={planet}
          center={centers[index]}
          radius={radius}
          selected={selectedId === String(planet.planet_id)}
          locale={locale}
        />
      ))}
    </g>
  );
}

function Planet({
  planet,
  center,
  radius,
  selected,
  locale,
}: {
  planet: KundaliPlanet;
  center: Point;
  radius: number;
  selected: boolean;
  locale: ChartLocale;
}) {
  const id = String(planet.planet_id).toLowerCase();
  const label = PLANET_ABBREVIATIONS[locale][id] ?? id.slice(0, 2).toUpperCase();
  return (
    <g
      className={[
        "jyotish-kundali__planet",
        selected ? "is-selected" : "",
        radius < 10 ? "is-compact" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden
    >
      <circle cx={center[0]} cy={center[1]} r={radius} />
      <text x={center[0]} y={center[1]}>
        {label}
      </text>
    </g>
  );
}

function PlanetDetail({
  planet,
  copy,
  locale,
  unflaggedDignity,
}: {
  planet: KundaliPlanet | null;
  copy: ChartCopy;
  locale: ChartLocale;
  unflaggedDignity: NonNullable<KundaliChartProps["unflaggedDignity"]>;
}) {
  if (!planet) {
    return <p className="jyotish-kundali__hint">{copy.hint}</p>;
  }
  const statuses = [
    planet.is_retrograde ? copy.retrograde : null,
    planet.is_combust ? copy.combust : null,
    planet.is_exalted ? copy.exalted : null,
    planet.is_debilitated ? copy.debilitated : null,
  ].filter(Boolean);
  // "Standard dignity" claims the graha is neither exalted nor debilitated:
  // printed when both flags say so, or, in the default "standard" reading,
  // when the caller gives no flags at all.
  const dignityKnown =
    typeof planet.is_exalted === "boolean" && typeof planet.is_debilitated === "boolean";
  const status =
    statuses.join(" · ") || (dignityKnown || unflaggedDignity === "standard" ? copy.standard : "");
  return (
    <div className="jyotish-kundali__detail" role="status" aria-live="polite">
      <div>
        <strong>{grahaName(String(planet.planet_id), locale)}</strong>
        <span>
          {copy.house} {planet.house ?? "—"}
          {planet.rashi ? ` · ${planet.rashi}` : ""}
          {planet.nakshatra ? ` · ${planet.nakshatra}` : ""}
        </span>
      </div>
      {status ? <span>{status}</span> : null}
    </div>
  );
}

function groupPlanetsByHouse(planets: readonly KundaliPlanet[]): Record<number, KundaliPlanet[]> {
  const grouped = Object.fromEntries(
    Array.from({ length: 12 }, (_, index) => [index + 1, [] as KundaliPlanet[]])
  ) as Record<number, KundaliPlanet[]>;
  for (const planet of planets) {
    if (planet.house && planet.house >= 1 && planet.house <= 12) grouped[planet.house].push(planet);
  }
  return grouped;
}

export default KundaliChart;
